export interface Ref {
  id: bigint;
  createdAt: Date;
}

export enum RefStage {
  Local = "local",
  Shared = "shared",
  Develop = "develop",
  Demo = "demo",
  Staging = "staging",
  Production = "production",
}

export enum GlobalPrivilege {
  // Auth
  Auth_Grant = "AUTH::GRANT", // Auth 변경 및 모든 권한
  Auth_View = "AUTH::VIEW", // Auth 관리 페이지 Viewer 사용권한

  // Admin
  Admin_User_Create = "ADMIN::USER:CREATE",
  Admin_User_UnMasked = "ADMIN::USER:UNMASKED",
  Admin_Comments_Edit = "ADMIN::COMMENTS:EDIT",
  Admin_Release_Edit = "ADMIN::RELEASE:EDIT",
  Admin_Notice_Add = "ADMIN::NOTICE:ADD",
  Admin_Notice_Edit = "ADMIN::NOTICE:EDIT",
  Admin_Current_Subscription_Edit = "ADMIN::CURRENT_SUBSCRIPTION:EDIT",
  Admin_Next_Billing_Date_Edit = "ADMIN::NEXT_BILLING_DATE:EDIT",
  Admin_Organization_Dictionary_License_Edit = "ADMIN::ORGANIZATION:DICTIONARY_LICENSE:EDIT",
  Admin_Organization_View = "ADMIN::ORGANIZATION:VIEW",
  Admin_Organization_List_View = "ADMIN::ORGANIZATION_LIST:VIEW",
  Admin_Organization_Name_Edit = "ADMIN::ORGANIZATION_NAME:EDIT",
  Admin_Organization_Owner_Edit = "ADMIN::ORGANIZATION_OWNER:EDIT",
  Admin_Organization_Payment_Edit = "ADMIN:ORGANIZATION_PAYMENT:EDIT",
  Admin_Organization_Purchase_Edit = "ADMIN::ORGANIZATION_PURCHASE:EDIT",
  Admin_Subscription_Supervisor = "ADMIN::SUBSCRIPTION:SUPERVISOR",
  Admin_Subscription_View = "ADMIN::SUBSCRIPTION:VIEW",
  Admin_Subscription_Edit = "ADMIN::SUBSCRIPTION:EDIT",
  Admin_Subscription_CleanUp_Date_Edit = "ADMIN::SUBSCRIPTION_CLEANUP_DATE:EDIT",
  Admin_Subscription_History_View = "ADMIN::SUBSCRIPTION_HISTORY:VIEW",
  Admin_Audit_Trail_View = "ADMIN::AUDIT_TRAIL:VIEW",
  Admin_Property_View = "ADMIN::PROPERTY:VIEW",
  Admin_Property_Edit = "ADMIN::PROPERTY:EDIT",
  Admin_FeatureFlag_View = "ADMIN::FEATURE_FLAG:VIEW",
  Admin_FeatureFlag_Edit = "ADMIN::FEATURE_FLAG:EDIT",
  Admin_AutomationTest = "ADMIN::AUTOMATION_TEST",

  // Admin-Safety
  Admin_Safety_Workspace_View = "ADMIN::SAFETY:WORKSPACE:VIEW",
  Admin_Safety_Workspace_Edit = "ADMIN::SAFETY:WORKSPACE:EDIT",
  Admin_Safety_Template_View = "ADMIN::SAFETY:TEMPLATE:VIEW",
  Admin_Safety_Manage_Gateway_View = "ADMIN::SAFETY:MANAGE_GATEWAY:VIEW",
  Admin_Safety_Manage_Gateway_Edit = "ADMIN::SAFETY:MANAGE_GATEWAY:EDIT",

  // Admin-Docs
  Admin_Docs_Document_Sign_Supervisor = "ADMIN::DOCS:DOCUMENT_SIGN:SUPERVISOR",

  // Admin-VDR
  Admin_Vdr_Supervisor = "ADMIN::VDR:SUPERVISOR",
  Admin_Vdr_Room_UnMasked = "ADMIN::VDR:ROOM:UNMASKED",
  Admin_Vdr_RoomMember_UnMasked = "ADMIN::VDR:ROOM_MEMBER:UNMASKED",

  // Project
  Project_Project_Supervisor = "PROJECT::PROJECT:SUPERVISOR", // Project Supervisor
  Project_Folder_Supervisor = "PROJECT::FOLDER:SUPERVISOR", // Folder Supervisor

  // Docs
  Docs_Viewer = "DOCS::VIEWER", // Viewer 사용권한
  Docs_Sign = "DOCS::SIGN", // eSign 사용권한
  Docs_Composer = "DOCS::COMPOSER", // Composer 사용권한
  Docs_Composer_Supervisor = "DOCS::COMPOSER:SUPERVISOR", // Composer Supervisor
  Docs_Template = "DOCS::TEMPLATE", // Composer - Template 사용권한

  // Builder
  Builder_Supervisor = "BUILDER::SUPERVISOR", // Builder Supervisor
  Builder_Payload_Export = "BUILDER::PAYLOAD_EXPORT", // Builder Payload Export 사용권한

  // Safety
  Safety_Supervisor = "SAFETY::SUPERVISOR", // Safety Supervisor

  // Converter
  Converter_Supervisor = "CONVERTER::SUPERVISOR", // Converter Supervisor
}

export enum RefServiceType {
  Ref = "REF",
  Suite = "SUITE",
  Resource = "RESOURCE",
  SSO = "SSO",
  Portal = "PORTAL",
  ERecruitment = "ERECRUITMENT",
  Auth = "AUTH",
  Admin = "ADMIN",
  Project = "PROJECT",
  Billing = "BILLING",
  Docs = "DOCS",
  Tmf = "TMF",
  DocsPdf = "DOCS_PDF",
  Builder = "BUILDER",
  Validator = "VALIDATOR",
  Safety = "SAFETY",
  OCR = "OCR",
  Paths = "PATHS",
  Cdms = "CDMS",
  CdmsStudy = "CDMS_STUDY",
  CdmsDw = "CDMS_DW",
  Vdr = "VDR",
  Converter = "CONVERTER",
  ConverterValidator = "CONVERTER_VALIDATOR",
  ConverterXslt = "CONVERTER_XSLT",
  Ctd = "CTD",
  X = "X",
  TNE = "TNE",
  Monitor = "MONITOR",
  RTSM = "RTSM",
  Lisa = "LISA",
  LisaOrchestrator = "LISA_ORCHESTRATOR",
  LisaProtocolWriter = "LISA_PROTOCOL_WRITER",
  LisaDocxAmendGenerator = "LISA_DOCX_AMEND_GENERATOR",
}

export enum RefResourceType {
  // Common
  Common_EventPubQueue = "COMMON::EVENT_PUB_QUEUE",
  Common_EventSubQueue = "COMMON::EVENT_SUB_QUEUE",
  Common_EventPullQueue = "COMMON::EVENT_PULL_QUEUE",

  // Auth
  Auth_User = "AUTH::USER",
  Auth_UserInfo = "AUTH::USER_INFO",
  Auth_UserPrivilege = "AUTH::USER_PRIVILEGE",
  Auth_UserVerificationCode = "AUTH::USER_VERIFICATION_CODE",
  Auth_Organization = "AUTH::ORGANIZATION",
  Auth_Organization_Dictionary_License = "AUTH::ORGANIZATION_DICTIONARY_LICENSE",
  Auth_Member = "AUTH::MEMBER",
  Auth_MemberInvitation = "AUTH::MEMBER_INVITATION",
  Auth_Role = "AUTH::ROLE",
  Auth_UserRole = "AUTH::USER_ROLE",
  Auth_SalesInquiry = "AUTH::SALES_INQUIRY",
  Auth_Notification = "AUTH::NOTIFICATION",

  // Admin
  Admin_Comment = "ADMIN::COMMENT",
  Admin_Property = "ADMIN::PROPERTY",
  Admin_Notice = "ADMIN::NOTICE",
  Admin_NoticeContent = "ADMIN::NOTICE_CONTENT",
  Admin_Release = "ADMIN::RELEASE",
  Admin_ReleaseContent = "ADMIN::RELEASE_CONTENT",
  Admin_FeatureFlag = "ADMIN::FEATURE_FLAG",
  Admin_FeatureFlagGroup = "ADMIN::FEATURE_FLAG_GROUP",

  // SSO
  SSO_AuthToken = "SSO::AUTH_TOKEN",
  SSO_RequestToken = "SSO::REQUEST_TOKEN",
  SSO_Notification = "SSO::NOTIFICATION",
  SSO_UserNotification = "SSO::USER_NOTIFICATION",
  SSO_UserVerificationCode = "SSO::USER_VERIFICATION_CODE",
  SSO_UserAuthLog = "SSO::USER_AUTH_LOG",
  SSO_UserDevice = "SSO::USER_DEVICE",
  SSO_UserDeviceVerificationCode = "SSO::USER_DEVICE_VERIFICATION_CODE",

  // Resource
  Resource_PrivilegeGroup = "RESOURCE::PRIVILEGE_GROUP",
  Resource_Resource = "RESOURCE::RESOURCE",
  Resource_ResourcePrivilege = "RESOURCE::RESOURCE_PRIVILEGE",
  Resource_S3Resource = "RESOURCE::S3_RESOURCE",
  Resource_S3ZipPackage = "RESOURCE::S3_ZIP_PACKAGE",
  Resource_S3ZipContentsUpload = "RESOURCE::S3_ZIP_CONTENTS_UPLOAD",
  Resource_S3ZipContentsUploadItem = "RESOURCE::S3_ZIP_CONTENTS_UPLOAD_ITEM",
  Resource_S3Package = "RESOURCE::S3_PACKAGE",

  // Project
  Project_Project = "PROJECT::PROJECT",
  Project_Folder = "PROJECT::FOLDER",
  Project_FolderVariable = "PROJECT::FOLDER_VARIABLE", // Folder의 종속 리소스로 별도 권한 없음
  Project_OrgFolder = "PROJECT::ORG_FOLDER",

  // Billing
  Billing_BillingKey = "BILLING::BILLING_KEY",
  Billing_Bundle = "BILLING::BUNDLE",
  Billing_BundleItem = "BILLING::BUNDLE_ITEM",
  Billing_BundlePrice = "BILLING::BUNDLE_PRICE",
  Billing_BundleOrganizationPrice = "BILLING::BUNDLE_ORGANIZATION_PRICE",
  Billing_Subscription = "BILLING::SUBSCRIPTION",
  Billing_SubscriptionLicense = "BILLING::SUBSCRIPTION_LICENSE",
  Billing_SubscriptionCleanUp = "BILLING::SUBSCRIPTION_CLEAN_UP",
  Billing_Inventory = "BILLING::INVENTORY",
  Billing_InventoryUsage = "BILLING::INVENTORY_USAGE",
  Billing_Invoice = "BILLING::INVOICE",
  Billing_Transaction = "BILLING::TRANSACTION",
  Billing_SubscriptionStatusNoti = "BILLING::SUBSCRIPTION_STATUS_NOTI",
  Billing_NotificationMail = "BILLING::NOTIFICATION_MAIL",

  // Template
  Docs_Template = "DOCS::TEMPLATE",
  Docs_TemplateGrant = "DOCS::TEMPLATE_GRANT",

  // Folder
  Docs_Folder = "DOCS::FOLDER",
  Docs_FolderVariable = "DOCS::FOLDER_VARIABLE", // Folder의 종속 리소스로 별도 권한 없음
  Docs_FolderFile = "DOCS::FOLDER_FILE",
  Docs_FolderSharing = "DOCS::FOLDER_SHARING",
  Docs_FolderSharingSetting = "DOCS::FOLDER_SHARING_SETTING",
  Docs_FolderSharingIpWhitelist = "DOCS::FOLDER_SHARING_IP_WHITELIST",
  Docs_FolderSharingLink = "DOCS::FOLDER_SHARING_LINK",

  // Proposal
  Docs_Proposal = "DOCS::PROPOSAL",
  Docs_ProposalVersion = "DOCS::PROPOSAL_VERSION", // ProposalVersion의 종속 리소스로 별도 권한 없음
  Docs_ProposalSection = "DOCS::PROPOSAL_SECTION", // ProposalVersion의 종속 리소스로 별도 권한 없음
  Docs_ProposalTitle = "DOCS::PROPOSAL_TITLE", // ProposalVersion의 종속 리소스로 별도 권한 없음
  Docs_ProposalContent = "DOCS::PROPOSAL_CONTENT", // ProposalVersion의 종속 리소스로 별도 권한 없음
  Docs_ProposalComment = "DOCS::PROPOSAL_COMMENT", // ProposalVersion의 종속 리소스로 별도 권한 없음

  // Sign
  Docs_Sign = "DOCS::SIGN",
  Docs_SignAttr = "DOCS::SIGN_ATTR", // 종속 리소스로 별도 권한 없음
  Docs_SignToken = "DOCS::SIGN_TOKEN", // 종속 리소스로 별도 권한 없음
  Docs_Signer = "DOCS::SIGNER",

  Docs_DocumentSignReferrer = "DOCS::DOCUMENT_SIGN_REFERRER",
  Docs_Notification = "DOCS::NOTIFICATION",
  Docs_UserSignature = "DOCS::USER_SIGNATURE",

  // Document
  Docs_Document = "DOCS::DOCUMENT",
  Docs_DocumentSign = "DOCS::DOCUMENT_SIGN",
  Docs_DocumentSigner = "DOCS::DOCUMENT_SIGNER", // 종속 리소스로 별도 권한 없음
  Docs_DocumentSignerAttr = "DOCS::DOCUMENT_SIGNER_ATTR", // 종속 리소스로 별도 권한 없음
  Docs_DocumentSignerHistory = "DOCS::DOCUMENT_SIGNER_HISTORY", // 종속 리소스로 별도 권한 없음
  Docs_DocumentSignerAttrHistory = "DOCS::DOCUMENT_SIGNER_ATTR_HISTORY", // 종속 리소스로 별도 권한 없음
  Docs_DocumentTrainingRecord = "DOCS::DOCUMENT_TRAINING_RECORD",
  Docs_FileExportHistory = "DOCS::FILE_EXPORT_HISTORY",
  Docs_DocumentSignSchedule = "DOCS::DOCUMENT_SIGN_SCHEDULE",
  Docs_DocumentSignerSchedule = "DOCS::DOCUMENT_SIGNER_SCHEDULE",

  // E-Consent
  Docs_EconsentConfigure = "DOCS::ECONSENT_CONFIGURE",
  Docs_EconsentHistory = "DOCS::ECONSENT_HISTORY",
  Docs_EconsentSignerHistory = "DOCS::ECONSENT_SIGNER_HISTORY",

  // E-Consent Study
  Docs_EconsentCountry = "DOCS::ECONSENT_COUNTRY",
  Docs_EconsentStudyConfiguration = "DOCS::ECONSENT_STUDY_CONFIGURATION",
  Docs_EconsentStudyCountry = "DOCS::ECONSENT_STUDY_COUNTRY",
  Docs_EconsentStudySite = "DOCS::ECONSENT_STUDY_SITE",
  Docs_EconsentStudySiteName = "DOCS::ECONSENT_STUDY_SITE_NAME",
  Docs_EconsentStudyInvestigatorProfile = "DOCS::ECONSENT_STUDY_INVESTIGATOR_PROFILE",
  Docs_EconsentStudyUser = "DOCS::ECONSENT_STUDY_USER",
  Docs_EconsentStudyRole = "DOCS::ECONSENT_STUDY_ROLE",
  Docs_EconsentStudyRoleName = "DOCS::ECONSENT_STUDY_ROLE_NAME",
  Docs_EconsentStudyUserRole = "DOCS::ECONSENT_STUDY_USER_ROLE",
  Docs_EconsentStudyUserSite = "DOCS::ECONSENT_STUDY_USER_SITE",
  Docs_EconsentStudyRolePrivilege = "DOCS::ECONSENT_STUDY_ROLE_PRIVILEGE",
  Docs_EconsentSignerStudyIdentifier = "DOCS::ECONSENT_SIGNER_STUDY_IDENTIFIER",

  Docs_XIntegrationQueue = "DOCS::X_INTEGRATION_QUEUE",

  // E-TMF
  Tmf_Country = "TMF::COUNTRY",
  Tmf_Study = "TMF::STUDY",
  Tmf_StudyCountry = "TMF::STUDY_COUNTRY",
  Tmf_StudySite = "TMF::STUDY_SITE",
  Tmf_StudyUser = "TMF::STUDY_USER",
  Tmf_StudyRole = "TMF::STUDY_ROLE",
  Tmf_StudyRolePrivs = "TMF::STUDY_ROLE_PRIVILEGES",
  Tmf_StudyTemplate = "TMF::STUDY_TEMPLATE",
  Tmf_StudyStructure = "TMF::STUDY_STRUCTURE",
  Tmf_StudyMilestone = "TMF::STUDY_MILESTONE",
  Tmf_StudyMilestoneTimeline = "TMF::STUDY_MILESTONE_TIMELINE",
  Tmf_StudyMilestoneEssentialDocument = "TMF::STUDY_MILESTONE_ESSENTIAL_DOCUMENT",
  Tmf_StudyZone = "TMF::STUDY_ZONE",
  Tmf_StudySection = "TMF::STUDY_SECTION",
  Tmf_StudyArtifact = "TMF::STUDY_ARTIFACT",
  Tmf_StudySubArtifact = "TMF::STUDY_SUB_ARTIFACT",

  Tmf_StudyDocument = "TMF::STUDY_DOCUMENT",
  Tmf_StudyDocumentVersion = "TMF::STUDY_DOCUMENT_VERSION",
  Tmf_StudyDocumentComment = "TMF::STUDY_DOCUMENT_COMMENT",
  Tmf_StudyDocumentQcReq = "TMF::STUDY_DOCUMENT_QC_REQ",
  Tmf_StudyDocumentQcReviewer = "TMF::STUDY_DOCUMENT_QC_REVIEWER",
  Tmf_StudyDocumentReason = "TMF::STUDY_DOCUMENT_REASON",
  Tmf_StudyDocumentQcRejectionReasonList = "TMF::STUDY_DOCUMENT_QC_REJECTION_REASON_LIST",
  Tmf_StudyDocumentDeletionReasonList = "TMF::STUDY_DOCUMENT_DELETION_REASON_LIST",
  Tmf_StudyDocumentQcReviewerSetting = "TMF::STUDY_DOCUMENT_QC_REVIEWER_SETTING",
  Tmf_StudyDocumentQcReviewerSettingList = "TMF::STUDY_DOCUMENT_QC_REVIEWER_SETTING_LIST",

  Tmf_StudyDwHistory = "TMF::STUDY_DW_HISTORY",
  Tmf_StudyExport = "TMF::STUDY_EXPORT",
  Tmf_StudyReport = "TMF::STUDY_REPORT",

  // DocsPDF
  DocsPdf_Pdf = "DOCS_PDF::PDF",
  DocsPdf_Pdf_OwnerPassword = "DOCS_PDF::PDF_OWNER_PASSWORD",
  DocsPdf_CompressionQueue = "DOCS_PDF::COMPRESSION_QUEUE",
  DocsPdf_ConvertQueue = "DOCS_PDF::CONVERT_QUEUE",

  // DocsOfficePDF
  DocsOfficePdf_ConvertQueue = "DOCS_OFFICE_PDF::CONVERT_QUEUE",

  // Docs
  Docs_Contact = "DOCS::CONTACT",
  Docs_ContactGroup = "DOCS::CONTACT_GROUP",
  Docs_ContactUser = "DOCS::CONTACT_USER",

  // CDMS
  CDMS_MasterStudy = "CDMS::MASTER_STUDY",
  CDMS_StudyCrf = "CDMS::STUDY_CRF",
  CDMS_DW_History = "CDMS::DW_HISTORY",
  CDMS_DW_TemplateReport = "CDMS::DW_TEMPLATE_REPORT",
  CDMS_StudySite = "CDMS::STUDY_SITE",
  CDMS_Affiliation = "CDMS::AFFILIATION",
  CDMS_StudyIndependentEvaluation = "CDMS::STUDY_INDEPENDENT_EVALUATION",
  CDMS_StudyProtocolDeviation = "CDMS::STUDY_PROTOCOL_DEVIATION",
  CDMS_StudyProtocolDeviationCategory = "CDMS::STUDY_PROTOCOL_DEVIATION_CATEGORY",
  CDMS_StudyAttachmentFileUsage = "CDMS::STUDY_ATTACHMENT_FILE_USAGE",
  CDMS_StudyAttachmentFileUsageInfo = "CDMS::STUDY_ATTACHMENT_FILE_USAGE_INFO",
  CDMS_StudyLabTestStandardUnit = "CDMS::STUDY_LAB_TEST_STANDARD_UNIT",
  CDMS_StudyLabTestAlertNotification = "CDMS::STUDY_LAB_TEST_ALERT_NOTIFICATION",

  CDMS_StudyAlwaysSeriousListUpload = "CDMS::Study_ALWAYS_SERIOUS_LIST_UPLOAD",

  CDMS_StudyUser = "CDMS::STUDY_USER",
  CDMS_StudyRole = "CDMS::STUDY_ROLE",
  CDMS_SafetyIntegrationUser = "CDMS::SAFETY_INTEGRATION_USER",
  CDMS_SafetyCase = "CDMS::SAFETY_CASE",

  CDMS_Study = "CDMS::STUDY",
  CDMS_StudySubject = "CDMS::STUDY_SUBJECT",
  CDMS_StudyRandomCode = "CDMS::STUDY_RANDOM_CODE",
  CDMS_StudyIpCode = "CDMS::STUDY_IP_CODE",
  CDMS_StudyStatTimelinessBySite = "CDMS::STUDY_STAT_TIMELINESS_BY_SITE",
  CDMS_StudyStatTimelinessBySiteVisit = "CDMS::STUDY_STAT_TIMELINESS_BY_SITE_VISIT",

  CDMS_StudyRandomizationSiteStatus = "CDMS::STUDY_RANDOMIZATION_SITE_STATUS",

  // Safety
  Safety_AutoNarrative = "SAFETY::AUTO_NARRATIVE",
  Safety_AutoNarrativeRow = "SAFETY::AUTO_NARRATIVE_ROW",
  Safety_User = "SAFETY::USER",
  Safety_NotiMail = "SAFETY::NOTI_MAIL",
  Safety_Cache = "SAFETY::CACHE",
  Safety_Crf = "SAFETY::CRF",
  Safety_CrfExtension = "SAFETY::CRF_EXTENSION",
  Safety_CrfOrganization = "SAFETY::CRF_ORGANIZATION",
  Safety_Workspace = "SAFETY::WORKSPACE",
  Safety_WorkspaceItem = "SAFETY::WORKSPACE_ITEM",
  Safety_WorkspaceFolder = "SAFETY::WORKSPACE_FOLDER",
  Safety_WorkspaceMember = "SAFETY::WORKSPACE_MEMBER",
  Safety_WorkspaceMemberRole = "SAFETY::WORKSPACE_MEMBER_ROLE",
  Safety_WorkspaceRole = "SAFETY::WORKSPACE_ROLE",
  Safety_WorkspaceRolePrivilege = "SAFETY::WORKSPACE_ROLE_PRIVILEGE",
  Safety_WorkspaceDictionary = "SAFETY::WORKSPACE_DICTIONARY",
  Safety_WorkspaceCasePrivilege = "SAFETY::WORKSPACE_CASE_PRIVILEGE",
  Safety_WorkspaceDestroyTask = "SAFETY::WORKSPACE_DESTROY_TASK",
  Safety_WorkspaceDwHistory = "SAFETY::WORKSPACE_DW_HISTORY",
  Safety_Preset = "SAFETY::PRESET",
  Safety_PresetPrefilledForm = "SAFETY::PRESET_PREFILLED_FORM",
  Safety_PresetPrefilledFormCapture = "SAFETY::PRESET_PREFILLED_FORM_CAPTURE",
  Safety_Case = "SAFETY::CASE",
  Safety_CaseSync = "SAFETY::CASE_SYNC",
  Safety_CaseSyncBuffer = "SAFETY::CASE_SYNC_BUFFER",
  Safety_CaseSyncQueue = "SAFETY::CASE_SYNC_QUEUE",
  Safety_CaseExportUsage = "SAFETY::CASE_EXPORT_USAGE",
  Safety_CaseReport = "SAFETY::CASE_REPORT",
  Safety_CaseReportDictionary = "SAFETY::CASE_REPORT_DICTIONARY",
  Safety_CaseReportRequest = "SAFETY::CASE_REPORT_REQUEST",
  Safety_CaseReportRequestApprover = "SAFETY::CASE_REPORT_REQUEST_APPROVER",
  Safety_CaseReportAdverseEvent = "SAFETY::CASE_REPORT_ADVERSE_EVENT",
  Safety_CaseReportProduct = "SAFETY::CASE_REPORT_PRODUCT",
  Safety_CaseReportAssessment = "SAFETY::CASE_REPORT_ASSESSMENT",
  Safety_CaseReportSubject = "SAFETY::CASE_REPORT_SUBJECT",
  Safety_CaseReportImdrfCode = "SAFETY::CASE_REPORT_IMDRF_CODE",
  Safety_CaseReportWorkflow = "SAFETY::CASE_REPORT_WORKFLOW",
  Safety_CaseReportWorkflowAssignee = "SAFETY::CASE_REPORT_WORKFLOW_ASSIGNEE",
  Safety_CaseCapture = "SAFETY::CASE_CAPTURE",
  Safety_CaseComment = "SAFETY::CASE_COMMENT",
  Safety_CaseReportExport = "SAFETY::CASE_REPORT_EXPORT",
  Safety_CaseReportExportDetail = "SAFETY::CASE_REPORT_EXPORT_DETAIL",
  Safety_CaseReportImport = "SAFETY::CASE_REPORT_IMPORT",
  Safety_CaseReportImportAiExtraction = "SAFETY::CASE_REPORT_IMPORT_AI_EXTRACTION",
  Safety_CaseReportImportBatch = "SAFETY::CASE_REPORT_IMPORT_BATCH",
  Safety_CaseReportImportDictionary = "SAFETY::CASE_REPORT_IMPORT_DICTIONARY",
  Safety_CaseReportImportFile = "SAFETY::CASE_REPORT_IMPORT_FILE",
  Safety_CaseReportStatus = "SAFETY::CASE_REPORT_STATUS",
  Safety_GatewayCertificate = "SAFETY::GATEWAY_CERTIFICATE",
  Safety_GatewayAs2Account = "SAFETY::GATEWAY_AS2_ACCOUNT",
  Safety_GatewayAs2Connection = "SAFETY::GATEWAY_AS2_CONNECTION",
  Safety_Submission = "SAFETY::SUBMISSION",
  Safety_CaseReportSubmission = "SAFETY::CASE_REPORT_SUBMISSION",
  Safety_CaseReportSearch = "SAFETY::CASE_REPORT_SEARCH",
  Safety_CaseReportSearchSharedUser = "SAFETY::CASE_REPORT_SEARCH_SHARED_USER",
  Safety_CaseReportSearchSnapshot = "SAFETY::CASE_REPORT_SEARCH_SNAPSHOT",
  Safety_CaseReportExportCustomTemplate = "SAFETY::CASE_REPORT_EXPORT_CUSTOM_TEMPLATE",
  Safety_SignalDetectionProfile = "SAFETY::SIGNAL_DETECTION_PROFILE",
  Safety_SignalDetectionProfileTargetProduct = "SAFETY::SIGNAL_DETECTION_PROFILE_TARGET_PRODUCT",
  Safety_FaersProduct = "SAFETY::FAERS_PRODUCT",
  Safety_SignalDetectionRunHistory = "SAFETY::SIGNAL_DETECTION_RUN_HISTORY",
  Safety_SignalDetectionSignal = "SAFETY::SIGNAL_DETECTION_SIGNAL",
  Safety_SignalDetectionResult = "SAFETY::SIGNAL_DETECTION_RESULT",
  Safety_SignalDetectionWorkflowHistory = "SAFETY::SIGNAL_DETECTION_WORKFLOW_HISTORY",
  Safety_CaseNarrativeGeneration = "SAFETY::CASE_NARRATIVE_GENERATION",

  // OCR
  OCR_Template = "OCR::TEMPLATE",
  OCR_TemplateGrant = "OCR::TEMPLATE_GRANT",
  OCR_Document = "OCR::DOCUMENT",
  OCR_DocumentImage = "OCR::DOCUMENT_IMAGE",

  // Builder
  Builder_Study = "BUILDER::STUDY",
  Builder_StudyTemplate = "BUILDER::STUDY_TEMPLATE",
  Builder_StudyTemplatePayload = "BUILDER::STUDY_TEMPLATE_PAYLOAD",
  Builder_StudyTemplatePayloadRequest = "BUILDER::STUDY_TEMPLATE_PAYLOAD_REQUEST",
  Builder_StudyTemplateUser = "BUILDER::STUDY_TEMPLATE_USER",
  Builder_StudyTitle = "BUILDER::STUDY_TITLE",
  Builder_StudyEcoaConfig = "BUILDER::STUDY_ECOA_CONFIG",
  Builder_StudyPayload = "BUILDER::STUDY_PAYLOAD",
  Builder_StudyPayloadValidation = "BUILDER::STUDY_PAYLOAD_VALIDATION",
  Builder_StudyPayloadPushHistory = "BUILDER::STUDY_PAYLOAD_PUSH_HISTORY",
  Builder_StudyPayloadSyncHistory = "BUILDER::STUDY_PAYLOAD_SYNC_HISTORY",
  Builder_StudyPayloadComment = "BUILDER::STUDY_PAYLOAD_COMMENT",
  Builder_StudyPayloadDomain = "BUILDER::STUDY_PAYLOAD_DOMAIN",
  Builder_StudyPayloadFolder = "BUILDER::STUDY_PAYLOAD_FOLDER",
  Builder_StudyPayloadFolderVisit = "BUILDER::STUDY_PAYLOAD_FOLDER_VISIT",
  Builder_StudyPayloadFolderVisitPage = "BUILDER:STUDY_PAYLOAD_FOLDER_VISIT_PAGE",
  Builder_StudyPayloadPage = "BUILDER::STUDY_PAYLOAD_PAGE",
  Builder_StudyPayloadPageSection = "BUILDER::STUDY_PAYLOAD_PAGE_SECTION",
  Builder_StudyPayloadPageSectionItem = "BUILDER::STUDY_PAYLOAD_PAGE_SECTION_ITEM",
  Builder_StudyRole = "BUILDER::STUDY_ROLE",
  Builder_StudyRoleUpload = "BUILDER::STUDY_ROLE_UPLOAD",
  Builder_StudyRolePrivilege = "BUILDER::STUDY_ROLE_PRIVILEGE",
  Builder_StudyMedicalCoding = "BUILDER::STUDY_MEDICAL_CODING",
  Builder_StudyMedicalCodingColumn = "BUILDER::STUDY_MEDICAL_CODING_COLUMN",
  /** @deprecated CDMS_StudySite 사용 요망 */
  Builder_StudySite = "BUILDER::STUDY_SITE",
  Builder_StudySiteEnvironment = "BUILDER::STUDY_SITE_ENVIRONMENT",
  Builder_StudyDashboardConfig = "BUILDER::STUDY_DASHBOARD_CONFIG",
  Builder_StudyDashboardConfigSubjectStatus = "BUILDER::STUDY_DASHBOARD_CONFIG_SUBJECT_STATUS",
  Builder_StudyDashboardConfigSite = "BUILDER::STUDY_DASHBOARD_CONFIG_SITE",
  Builder_StudyStatusCaptureStatus = "BUILDER::STUDY_STATUS_CAPTURE_STAT",
  Builder_StudyLabTestItem = "BUILDER::STUDY_LAB_TEST_ITEM",
  Builder_StudyLabTestItemUnit = "BUILDER::STUDY_LAB_TEST_ITEM_UNIT",
  Builder_StudyPayloadLabTestItem = "BUILDER::STUDY_PAYLOAD_LAB_TEST_ITEM",
  Builder_StudyPayloadLabTestUnit = "BUILDER::STUDY_PAYLOAD_LAB_TEST_UNIT",
  Builder_StudyPayloadTrigger = "BUILDER::STUDY_PAYLOAD_TRIGGER",
  Builder_StudyPayloadTriggerVersion = "BUILDER::STUDY_PAYLOAD_TRIGGER_VERSION",
  Builder_StudyBuilderUser = "BUILDER::STUDY_BUILDER_USER",
  Builder_StudyBuilderUserRole = "BUILDER::STUDY_BUILDER_USER_ROLE",
  Builder_StudyBuilderRole = "BUILDER::STUDY_BUILDER_ROLE",
  Builder_StudyBuilderRolePrivilege = "BUILDER::STUDY_BUILDER_ROLE_PRIVILEGE",
  Builder_MemberOrganizationRole = "BUILDER::MEMBER_ORGANIZATION_ROLE",
  Builder_OrganizationRole = "BUILDER::ORGANIZATION_ROLE",
  Builder_OrganizationRolePrivilege = "BUILDER::ORGANIZATION_ROLE_PRIVILEGE",
  Builder_TriggerTargetMapping = "BUILDER::TRIGGER_TARGET_MAPPING",

  // Paths
  Paths_ShortenUrl = "PATHS::SHORTEN_URL",

  // eRecruitment
  ERecruitment_User = "ERECRUITMENT::USER",
  ERecruitment_UserInfo = "ERECRUITMENT::USER_INFO",
  ERecruitment_UserPrivilege = "ERECRUITMENT::USER_PRIVILEGE",
  ERecruitment_UserConsentHistory = "ERECRUITMENT::USER_CONSENT_HISTORY",
  ERecruitment_UserVerificationCode = "ERECRUITMENT::USER_VERIFICATION_CODE",
  ERecruitment_UserFavoriteStudy = "ERECRUITMENT::USER_FAVORITE_STUDY",
  ERecruitment_UserWithdrawalReason = "ERECRUITMENT::USER_WITHDRAWAL_REASON",
  ERecruitment_UserWithdrawalEmail = "ERECRUITMENT::USER_WITHDRAWAL_EMAIL",
  ERecruitment_UserOAuth2Info = "ERECRUITMENT::USER_OAUTH2_INFO",
  ERecruitment_AuthToken = "ERECRUITMENT::AUTH_TOKEN",
  ERecruitment_Category = "ERECRUITMENT::CATEGORY",
  ERecruitment_Location = "ERECRUITMENT::LOCATION",
  ERecruitment_Affiliation = "ERECRUITMENT::AFFILIATION",
  ERecruitment_Study = "ERECRUITMENT::STUDY",
  ERecruitment_StudyExternalLink = "ERECRUITMENT::STUDY_EXTERNAL_LINK",
  ERecruitment_StudyAdditionalContent = "ERECRUITMENT::STUDY_ADDITIONAL_CONTENT",
  ERecruitment_StudyCategory = "ERECRUITMENT::STUDY_CATEGORY",
  ERecruitment_StudySponsor = "ERECRUITMENT::STUDY_SPONSOR",
  ERecruitment_StudySite = "ERECRUITMENT::STUDY_SITE",
  ERecruitment_StudyInquiry = "ERECRUITMENT::STUDY_INQUIRY",
  ERecruitment_StudyAttachment = "ERECRUITMENT::STUDY_ATTACHMENT",
  ERecruitment_PrescreeningConsent = "ERECRUITMENT::PRESCREENING_CONSENT",
  ERecruitment_PrescreeningQuestionnaire = "ERECRUITMENT::PRESCREENING_QUESTIONNAIRE",
  ERecruitment_PrescreeningQuestionnaireQuestion = "ERECRUITMENT::PRESCREENING_QUESTIONNAIRE_QUESTION",
  ERecruitment_PrescreeningQuestionnaireAnswer = "ERECRUITMENT::PRESCREENING_QUESTIONNAIRE_ANSWER",
  ERecruitment_Notice = "ERECRUITMENT::NOTICE",
  ERecruitment_LegalConsent = "ERECRUITMENT::LEGAL_CONSENT",
  ERecruitment_FAQ = "ERECRUITMENT::FAQ",
  ERecruitment_CandidatePool = "ERECRUITMENT::CANDIDATE_POOL",
  ERecruitment_CandidatePoolInterestDisease = "ERECRUITMENT::CANDIDATE_POOL_INTEREST_DISEASE",
  ERecruitment_CandidatePoolProvince = "ERECRUITMENT::CANDIDATE_POOL_PROVINCE",
  ERecruitment_CandidatePoolCategory = "ERECRUITMENT::CANDIDATE_POOL_CATEGORY",
  ERecruitment_Participation = "ERECRUITMENT::PARTICIPATION",
  ERecruitment_ParticipationAnswer = "ERECRUITMENT::PARTICIPATION_ANSWER",
  ERecruitment_ParticipationStatusHistory = "ERECRUITMENT::PARTICIPATION_STATUS_HISTORY",
  ERecruitment_Space = "ERECRUITMENT::SPACE",
  ERecruitment_SpaceStudy = "ERECRUITMENT::SPACE_STUDY",
  ERecruitment_SpaceStudyMember = "ERECRUITMENT::SPACE_STUDY_MEMBER",
  ERecruitment_SpaceStudyMemberPrivilege = "ERECRUITMENT::SPACE_STUDY_MEMBER_PRIVILEGE",
  ERecruitment_SpaceMember = "ERECRUITMENT::SPACE_MEMBER",
  ERecruitment_Inquiry = "ERECRUITMENT::INQUIRY",
  ERecruitment_InquiryAnswer = "ERECRUITMENT::INQUIRY_ANSWER",
  ERecruitment_PartnershipInquiry = "ERECRUITMENT::PARTNERSHIP_INQUIRY",
  ERecruitment_PartnershipInquiryCategory = "ERECRUITMENT::PARTNERSHIP_INQUIRY_CATEGORY",
  ERecruitment_Notification = "ERECRUITMENT::NOTIFICATION",
  ERecruitment_NotificationFetchEvent = "ERECRUITMENT::NOTIFICATION_FETCH_EVENT",
  ERecruitment_Banner = "ERECRUITMENT::BANNER",

  // Portal
  Portal_User = "PORTAL::USER",
  Portal_UserInfo = "PORTAL::USER_INFO",
  Portal_UserPrivilege = "PORTAL::USER_PRIVILEGE",
  Portal_UserConsentHistory = "PORTAL::USER_CONSENT_HISTORY",
  Portal_UserVerificationCode = "PORTAL::USER_VERIFICATION_CODE",
  Portal_UserWithdrawalReason = "PORTAL::USER_WITHDRAWAL_REASON",
  Portal_UserWithdrawalEmail = "PORTAL::USER_WITHDRAWAL_EMAIL",
  Portal_UserOAuth2Info = "PORTAL::USER_OAUTH2_INFO",
  Portal_AuthToken = "PORTAL::AUTH_TOKEN",
  Portal_Notice = "PORTAL::NOTICE",
  Portal_LegalConsent = "PORTAL::LEGAL_CONSENT",
  Portal_FAQ = "PORTAL::FAQ",
  Portal_Inquiry = "PORTAL::INQUIRY",
  Portal_InquiryAnswer = "PORTAL::INQUIRY_ANSWER",

  // CONVERTER
  Converter_SdtmVariable = "CONVERTER::SDTM_VARIABLE",
  Converter_SdtmigDomain = "CONVERTER::SDTMIG_DOMAIN",
  Converter_SdtmigDomainVariable = "CONVERTER::SDTMIG_DOMAIN_VARIABLE",
  Converter_TerminologyVersion = "CONVERTER::CONTROLLED_TERMINOLOGY_VERSION",
  Converter_TerminologyCode = "CONVERTER::TERMINOLOGY_CODE",
  Converter_TerminologyTerm = "CONVERTER::TERMINOLOGY_TERM",
  Converter_ConversionFactorLibrary = "CONVERTER::CONVERSION_FACTOR_LIBRARY",
  Converter_CdiscFoundationalStandard = "CONVERTER::CDISC_FOUNDATIONAL_STANDARD",
  Converter_AdamDataStructure = "CONVERTER::ADAM_DATA_STRUCTURE",
  Converter_AdamVariableSet = "CONVERTER::ADAM_VARIABLE_SET",
  Converter_AdamVariable = "CONVERTER::ADAM_VARIABLE",
  Converter_Library = "CONVERTER::LIBRARY",

  Converter_Study = "CONVERTER::STUDY",
  Converter_StudyRawDataset = "CONVERTER::STUDY_RAW_DATASET",
  Converter_StudyRawDatasetResource = "CONVERTER::STUDY_RAW_DATASET_RESOURCE",
  Converter_StudyCrf = "CONVERTER::STUDY_CRF",
  Converter_StudyCrfAttr = "CONVERTER::STUDY_CRF_ATTR",
  Converter_StudyCrfLabel = "CONVERTER::STUDY_CRF_LABEL",
  Converter_StudyCrfExport = "CONVERTER::STUDY_CRF_EXPORT",
  Converter_StudyValidationReport = "CONVERTER::STUDY_VALIDATION_REPORT",
  Converter_StudyValidationReportFile = "CONVERTER::STUDY_VALIDATION_REPORT_FILE",
  Converter_StudyTerminologyCode = "CONVERTER::STUDY_TERMINOLOGY_CODE",
  Converter_StudyTerminologyTerm = "CONVERTER::STUDY_TERMINOLOGY_TERM",
  Converter_StudyConversionFactorSet = "CONVERTER::STUDY_CONVERSION_FACTOR_SET",
  Converter_StudyConversionFactor = "CONVERTER::STUDY_CONVERSION_FACTOR",
  Converter_StudyConversionExcelExport = "CONVERTER::STUDY_CONVERSION_FACTOR_EXCEL_EXPORT",
  Converter_StudyDefineXml = "CONVERTER::STUDY_DEFINE_XML",
  Converter_StudyDefineXmlInformation = "CONVERTER::STUDY_DEFINE_XML_INFORMATION",
  Converter_StudyDefineXmlStandard = "CONVERTER::STUDY_DEFINE_XML_STANDARD",
  Converter_StudyDefineXmlDictionary = "CONVERTER::STUDY_DEFINE_XML_DICTIONARY",
  Converter_StudyDefineXmlDataset = "CONVERTER::STUDY_DEFINE_XML_DATASET",
  Converter_StudyDefineXmlVariable = "CONVERTER::STUDY_DEFINE_XML_VARIABLE",
  Converter_StudyDefineXmlMetadata = "CONVERTER::STUDY_DEFINE_XML_METADATA",
  Converter_StudyDefineXmlCodelist = "CONVERTER::STUDY_DEFINE_XML_CODELIST",
  Converter_StudyDefineXmlDocument = "CONVERTER::STUDY_DEFINE_XML_DOCUMENT",
  Converter_StudyDefineXmlMethod = "CONVERTER::STUDY_DEFINE_XML_METHOD",
  Converter_StudyDefineXmlComment = "CONVERTER::STUDY_DEFINE_XML_COMMENT",
  Converter_StudyDefineXmlResultDisplay = "CONVERTER::STUDY_DEFINE_XML_RESULT_DISPLAY",
  Converter_StudyDefineXmlAnalysisResult = "CONVERTER::STUDY_DEFINE_XML_ANALYSIS_RESULT",
  Converter_StudyDataFlow = "CONVERTER::STUDY_DATA_FLOW",
  Converter_StudyDataFlowDataset = "CONVERTER::STUDY_DATA_FLOW_DATASET",
  Converter_StudyDataFlowRef = "CONVERTER::STUDY_DATA_FLOW_REF",
  Converter_StudyResource = "CONVERTER::STUDY_RESOURCE",
  Converter_StudyReportExcelExport = "CONVERTER::STUDY_REPORT_EXCEL_EXPORT",
  Converter_StudyValidationReportExport = "CONVERTER::STUDY_VALIDATION_REPORT_EXPORT",
  Converter_StudyTerminologyCodeExport = "CONVERTER::STUDY_TERMINOLOGY_CODE_EXCEL_EXPORT",
  Converter_StudyExportHistory = "CONVERTER::STUDY_EXPORT_HISTORY",
  Converter_StudyMember = "CONVERTER::STUDY_MEMBER",
  Converter_StudyMemberInvitation = "CONVERTER::STUDY_MEMBER_INVITATION",
  Converter_StudyRole = "CONVERTER::STUDY_ROLE",
  Converter_StudyRolePrivilege = "CONVERTER::STUDY_ROLE_PRIVILEGE",
  Converter_StudyAdamTargetDataset = "CONVERTER::STUDY_ADAM_TARGET_DATASET",
  Converter_StudyAdamTargetDatasetVariable = "CONVERTER::STUDY_ADAM_TARGET_DATASET_VARIABLE",
  Converter_StudyAdamPairVariable = "CONVERTER::STUDY_ADAM_PAIR_VARIABLE",
  // TODO: 마이그레이션 필요
  Converter_StudySdtmTargetDataset = "CONVERTER::TARGET_DATASET",
  Converter_StudySdtmTargetDatasetVariable = "CONVERTER::TARGET_DATASET_VARIABLE",
  Converter_StudySdtmCandidateDataset = "CONVERTER::CANDIDATE_DATASET",
  Converter_StudySdtmCandidateDomain = "CONVERTER::CANDIDATE_DOMAIN",

  // VDR
  Vdr_Room = "VDR::ROOM",
  Vdr_Room_Ai_Search_History = "VDR::ROOM::AI_SEARCH_HISTORY",
  Vdr_RoomSettingTerm = "VDR::ROOM_SETTING_TERM",
  Vdr_RoomSettingWatermark = "VDR::ROOM_SETTING_WATERMARK",
  Vdr_RoomRole = "VDR::ROOM_ROLE",
  Vdr_RoomRoleSystemPrivilege = "VDR::ROOM_ROLE_SYSTEM_PRIVILEGE",
  Vdr_RoomGroup = "VDR::ROOM_GROUP",
  Vdr_RoomUser = "VDR::ROOM_USER",
  Vdr_RoomResource = "VDR::ROOM_RESOURCE",
  Vdr_RoomResourceAccessLog = "VDR::ROOM_RESOURCE_ACCESS_LOG",
  Vdr_RoomRoleResourcePrivilege = "VDR::ROOM_ROLE_RESOURCE_PRIVILEGE",
  Vdr_RoomQuestionTeam = "VDR::ROOM_QUESTION_TEAM",
  Vdr_RoomQuestionTeamMember = "VDR::ROOM_QUESTION_TEAM_MEMBER",
  Vdr_RoomQuestionThread = "VDR::ROOM_QUESTION_QUESTION_THREAD",
  Vdr_RoomQuestionThreadView = "VDR::ROOM_QUESTION_THREAD_VIEW",
  Vdr_RoomQuestionThreadComment = "VDR::ROOM_QUESTION_THREAD_COMMENT",
  Vdr_RoomQuestionThreadCommentAttachment = "VDR::ROOM_QUESTION_THREAD_COMMENT_ATTACHMENT",
  Vdr_RoomQuestionSetting = "VDR::ROOM_QUESTION_SETTING",
  Vdr_RoomDwHistory = "VDR::ROOM_DW_HISTORY",

  // X
  X_Notification = "X::NOTIFICATION",
  X_NotificationMail = "X::NOTIFICATION_MAIL",
  X_ClinicalField = "X::CLINICAL_FIELD",
  X_Folder = "X::FOLDER",
  X_FolderDocument = "X::FOLDER_DOCUMENT",
  X_Variable = "X::VARIABLE",
  X_WorkspaceMember = "X::WORKSPACE_MEMBER",
  X_WorkspaceMemberPrivilege = "X::WORKSPACE_MEMBER_PRIVILEGE",
  X_WorkspaceMemberJob = "X::WORKSPACE_MEMBER_JOB",
  X_Job = "X::JOB",
  X_Workgroup = "X::WORKGROUP",
  X_Role = "X::ROLE",
  X_UserRole = "X::USER_ROLE",
  X_TaskLabel = "X::TASK_LABEL",
  X_TaskLabelUpload = "X::TASK_LABEL_UPLOAD",
  X_Unit = "X::UNIT",
  X_Affiliation = "X::AFFILIATION",
  X_AffiliationLang = "X::AFFILIATION_LANG",
  X_AffiliationDepartment = "X::AFFILIATION_DEPARTMENT",
  X_AffiliationDepartmentLang = "X::AFFILIATION_DEPARTMENT_LANG",
  X_AffiliationContact = "X::AFFILIATION_DEPARTMENT_CONTACT",
  X_AffiliationContactLang = "X::AFFILIATION_DEPARTMENT_CONTACT_LANG",
  X_AffiliationSiteExtend = "X::AFFILIATION_SITE_EXTEND",
  X_Contract = "X::CONTRACT",
  X_ContractPersonInCharge = "X::CONTRACT_PERSON_IN_CHARGE",
  X_ContractBilling = "X::CONTRACT_BILLING",
  X_ContractScope = "X::CONTRACT_SCOPE",
  X_ContractTask = "X::CONTRACT_TASK",
  X_ContractTaskRole = "X::CONTRACT_TASK_ROLE",
  /** @deprecated since 0.0.5.8 */
  X_ContractTaskAssignee = "X::CONTRACT_TASK_ASSIGNEE",
  X_ScopeTemplate = "X::SCOPE_TEMPLATE",
  X_ScopeTemplateRole = "X::SCOPE_TEMPLATE_ROLE",
  X_ScopeTemplateOrder = "X::SCOPE_TEMPLATE_ORDER",
  X_StandardTaskVersion = "X::STANDARD_TASK_VERSION",
  X_TaskTemplate = "X::TASK_TEMPLATE",
  X_TaskTemplateActionItem = "X::TASK_TEMPLATE_ACTION_ITEM",
  X_TaskTemplateCustomField = "X::TASK_TEMPLATE_CUSTOM_FIELD",
  X_TaskTemplateRelation = "X::TASK_TEMPLATE_RELATION",
  X_TaskTemplateSignee = "X::TASK_TEMPLATE_SIGNEE",
  X_TaskTemplateDocument = "X::TASK_TEMPLATE_DOCUMENT",
  X_Project = "X::PROJECT",
  X_ProjectActivity = "X::PROJECT_ACTIVITY",
  X_ProjectCountry = "X::PROJECT_COUNTRY",
  X_ProjectComment = "X::PROJECT_COMMENT",
  X_ProjectCommentFile = "X::PROJECT_COMMENT_FILE",
  X_ProjectCommentUserMention = "X::PROJECT_COMMENT_USER_MENTION",
  X_ProjectScope = "X::PROJECT_SCOPE",
  X_ProjectMember = "X::PROJECT_MEMBER",
  X_ProjectStudyTitle = "X::PROJECT_STUDY_TITLE",
  X_ProjectIntegration = "X::PROJECT_INTEGRATION",
  X_ProjectSiteStatusCustomField = "X::PROJECT_SITE_STATUS_CUTSOM_FIELD",
  X_ProjectSiteStatusSelectedStandard = "X::PROJECT_SITE_STATUS_SELECTED_STANDARD",
  X_ProjectSite = "X::PROJECT_SITE",
  X_ProjectSiteCra = "X::PROJECT_SITE_CRA",
  X_ProjectMilestone = "X::PROJECT_MILESTONE",
  X_ProjectTask = "X::PROJECT_TASK",
  X_ProjectTaskAttachment = "X::PROJECT_TASK_ATTACHMENT",
  X_ProjectTaskAttachmentEsign = "X::PROJECT_TASK_ATTACHMENT_ESIGN",
  X_ProjectTaskAttachmentEsignSignee = "X::PROJECT_TASK_ATTACHMENT_ESIGN_SIGNEE",
  X_ProjectTaskActionItem = "X::PROJECT_TASK_ACTION_ITEM",
  X_ProjectTaskCustomField = "X::PROJECT_TASK_CUSTOM_FIELD",
  X_ProjectTaskCustomFieldValue = "X::PROJECT_TASK_CUSTOM_FIELD_VALUE",
  X_ProjectTaskRelation = "X::PROJECT_TASK_RELATION",
  X_ProjectTaskAssignee = "X::PROJECT_TASK_ASSIGNEE",
  X_ProjectTaskSignee = "X::PROJECT_TASK_SIGNEE",
  X_ProjectTaskDocument = "X::PROJECT_TASK_DOCUMENT",
  X_ProjectTaskDocumentAutomation = "X::PROJECT_TASK_DOCUMENT_AUTOMATION",
  X_ProjectTaskRecurrence = "X::PROJECT_TASK_RECURRENCE",
  X_Approval = "X::APPROVAL",
  X_ApprovalStep = "X::APPROVAL_STEP",
  X_ApprovalStepAssignee = "X::APPROVAL_STEP_ASSIGNEE",
  X_ApprovalStepAssigneeMember = "X::APPROVAL_STEP_ASSIGNEE_MEMBER",
  X_ApprovalProjectMember = "X::APPROVAL_PROJECT_MEMBER",
  X_ApprovalReference = "X::APPROVAL_REFERENCE",
  X_ApprovalReferenceMember = "X::APPROVAL_REFERENCE_MEMBER",
  X_ApprovalHistory = "X::APPROVAL_HISTORY",
  X_ApprovalPreset = "X::APPROVAL_PRESET",
  X_ApprovalPresetReference = "X::APPROVAL_PRESET_REFERENCE",
  X_ApprovalPresetStepAssignee = "X::APPROVAL_PRESET_STEP_ASSIGNEE",
  X_ProjectMemberCandidate = "X::PROJECT_MEMBER_CANDIDATE",
  X_LaborRatePerPosition = "X::LABOR_RATE_PER_POSITION",
  X_Holiday = "X::HOLIDAY",
  X_HolidayLang = "X::HOLIDAY_LANG",

  Monitor_AccessCode = "MONITOR::ACCESS_CODE",
  Monitor_AuthToken = "MONITOR::AUTH_TOKEN",
  Monitor_Email = "MONITOR::EMAIL",
  Monitor_EcgData = "MONITOR::ECG_DATA",
  Monitor_ExerciseData = "MONITOR::EXERCISE_DATA",
  Monitor_Notification = "MONITOR::NOTIFICATION",
  Monitor_SmsQueue = "MONITOR::SMS_QUEUE",
  Monitor_Study = "MONITOR::STUDY",
  Monitor_DataImportRequest = "MONITOR::DATA_IMPORT_REQUEST",
  Monitor_DataSave = "MONITOR::DATA_SAVE",
  Monitor_DataValidationResult = "MONITOR::DATA_VALIDATION_RESULT",
  Monitor_StudyUser = "MONITOR::STUDY_USER",
  Monitor_Subject = "MONITOR::SUBJECT",
  Monitor_User = "MONITOR::USER",
  Monitor_UserData = "MONITOR::USER_DATA",
  Monitor_HealthData = "MONITOR::HEALTH_DATA",

  // RTSM
  RTSM_Study = "RTSM::STUDY",
  RTSM_StudyRole = "RTSM::STUDY_ROLE",
  RTSM_StudyUserRole = "RTSM::STUDY_USER_ROLE",
  RTSM_StudyUser = "RTSM::STUDY_USER",
  RTSM_StudyUserRoleSite = "RTSM::STUDY_USER_ROLE_SITE",
  RTSM_StudyRolePrivilege = "RTSM::STUDY_ROLE_PRIVILEGE",
  RTSM_StudyDepot = "RTSM::STUDY_DEPOT",
  RTSM_StudyDepotSite = "RTSM::STUDY_DEPOT_SITE",
  RTSM_StudySite = "RTSM::STUDY_SITE",
  RTSM_EventSub = "RTSM::EVENT_SUB",
  RTSM_StudySubject = "RTSM::STUDY_SUBJECT",
  RTSM_StudySubjectCustomData = "RTSM::STUDY_SUBJECT_CUSTOM_DATA",
  RTSM_StudyVisitPath = "RTSM::STUDY_VISIT_PATH",
  RTSM_StudyRandomizationSetting = "RTSM::STUDY_RANDOMIZATION_SETTING",
  RTSM_StudyIpSetting = "RTSM::STUDY_IP_SETTING",
  RTSM_Randomization = "RTSM::RANDOMIZATION",
  RTSM_RandomStratification = "RTSM::RANDOM_STRATIFICATION",
  RTSM_RandomStratificationDetail = "RTSM::RANDOM_STRATIFICATION_DETAIL",
  RTSM_RandomGroup = "RTSM::RANDOM_GROUP",
  RTSM_RandomIsolation = "RTSM::RANDOM_ISOLATION",
  RTSM_RandomCodeUpload = "RTSM::RANDOM_CODE_UPLOAD",
  RTSM_RandomCode = "RTSM::RANDOM_CODE",
  RTSM_RandomizationPeriod = "RTSM::RANDOMIZATION_PERIOD",
  RTSM_RandomizationPeriodVisit = "RTSM::RANDOMIZATION_PERIOD_VISIT",
  RTSM_IpCategory = "RTSM::IP_CATEGORY",
  RTSM_IpDrug = "RTSM::IP_DRUG",
  RTSM_IpSchedule = "RTSM::IP_SCHEDULE",
  RTSM_IpCode = "RTSM::IP_CODE",
  RTSM_IpCodeMetaData = "RTSM::IP_CODE_METADATA",
  RTSM_IpCodeUpload = "RTSM::IP_CODE_UPLOAD",
  RTSM_IpAutoDelivery = "RTSM::IP_AUTO_DELIVERY",
  RTSM_IpAutoDeliveryCondition = "RTSM::IP_AUTO_DELIVERY_CONDITION",
  RTSM_IpAutoDeliverySubCondition = "RTSM::IP_AUTO_DELIVERY_SUB_CONDITION",
  RTSM_IpAutoDeliverySubConditionDetail = "RTSM::IP_AUTO_DELIVERY_SUB_CONDITION_DETAIL",
  RTSM_IpAutoDeliveryRequest = "RTSM::IP_AUTO_DELIVERY_REQUEST",
  RTSM_IpAutoDeliveryRequestDetail = "RTSM::IP_AUTO_DELIVERY_REQUEST_DETAIL",
  RTSM_IpAutoDeliveryShippingPoint = "RTSM::IP_AUTO_DELIVERY_SHIPPING_POINT",
  RTSM_IpDelivery = "RTSM::IP_DELIVERY",
  RTSM_IpDeliveryDetail = "RTSM::IP_DELIVERY_DETAIL",
  RTSM_IpDeliveryIpCodeDetail = "RTSM::IP_DELIVERY_IP_CODE_DETAIL",
  RTSM_IpDeliveryRequestItem = "RTSM::IP_DELIVERY_REQUEST_ITEM",
  RTSM_IpAccountabilityBySite = "RTSM::IP_ACCOUNTABILITY_BY_SITE",
  RTSM_IpAccountabilityBySubject = "RTSM::IP_ACCOUNTABILITY_BY_SUBJECT",
  RTSM_StudyConfiguration = "RTSM::STUDY_CONFIGURATION",

  // LISA
  LisaOrchestrator_DocumentAutomation = "LISA_ORCHESTRATOR::DOCUMENT_AUTOMATION",
  LisaProtocolWriter_Protocol = "LISA_PROTOCOL_WRITER::PROTOCOL",
  LisaDocxAmendGenerator_Amendment = "LISA_DOCX_AMEND_GENERATOR::AMENDMENT",
  Lisa_History = "LISA::HISTORY",

  // RTSM
  RTSM_IpReturn = "RTSM::IP_RETURN",
  RTSM_IpReturnDetail = "RTSM::IP_RETURN_DETAIL",
  RTSM_StudyCustomComment = "RTSM::STUDY_CUSTOM_COMMENT",
  RTSM_StudyComment = "RTSM::STUDY_COMMENT",
  RTSM_StudyCustomQuery = "RTSM::STUDY_CUSTOM_QUERY",
  RTSM_StudyQuery = "RTSM::STUDY_QUERY",
  RTSM_NotificationEmail = "RTSM::NOTIFICATION_EMAIL",

  REF_Resource = "REF::RESOURCE",

  // TNE
  TNE_Role = "TNE::ROLE",
  TNE_RolePrivilege = "TNE::ROLE_PRIVILEGE",
  TNE_UserRole = "TNE::USER_ROLE",
  TNE_TneCode = "TNE::TNE_CODE",
  TNE_WorkLog = "TNE::WORK_LOG",
  TNE_WorkLogEntry = "TNE::WORK_LOG_ENTRY",
  TNE_WorkLogRejection = "TNE::WORK_LOG_REJECTION",
  TNE_Notification = "TNE::NOTIFICATION",
  TNE_NotificationMail = "TNE::NOTIFICATION_MAIL",
  TNE_Expense = "TNE::EXPENSE",
  TNE_ExpenseUpload = "TNE::EXPENSE_UPLOAD",
}

export enum RefStatus {
  Pending = "PENDING",
  Created = "CREATED",
  Deleted = "DELETED",
}

export enum ResourcePrivilege {
  Owner = "OWNER",
  Grant = "GRANT", // 권한을 부여하기 위한 권한 (RefResourceType의 Type과 1:1 대응되는 `${RefResourceType}:GRANT` 권한이 존재해야 함)
  Ghost = "GHOST",

  // Project
  Project_CreateProject = "PROJECT::CREATE_PROJECT",

  // Project > OrgFolder
  Project_OrgFolder_Grant = "PROJECT::ORG_FOLDER:GRANT",
  Project_OrgFolder_CreateFolder = "PROJECT::ORG_FOLDER:CREATE_FOLDER",
  Project_OrgFolder_View = "PROJECT::ORG_FOLDER:VIEW",
  Project_OrgFolder_Delete = "PROJECT::ORG_FOLDER:DELETE",

  // Project > Project
  Project_Project_Grant = "PROJECT::PROJECT:GRANT",
  Project_Project_CreateFolder = "PROJECT::PROJECT:CREATE_FOLDER",
  Project_Project_View = "PROJECT::PROJECT:VIEW",
  Project_Project_Delete = "PROJECT::PROJECT:DELETE",

  // Project > Folder
  Project_Folder_Grant = "PROJECT::FOLDER:GRANT",
  Project_Folder_CreateDocument = "PROJECT::FOLDER:CREATE_DOCUMENT",
  Project_Folder_CreateProposal = "PROJECT::FOLDER:CREATE_PROPOSAL",
  Project_Folder_CreateVariable = "PROJECT::FOLDER:CREATE_VARIABLE",
  Project_Folder_View = "PROJECT::FOLDER:VIEW",
  Project_Folder_ViewVariable = "PROJECT::FOLDER:VIEW_VARIABLE",
  Project_Folder_Edit = "PROJECT::FOLDER:EDIT",
  Project_Folder_EditVariable = "PROJECT::FOLDER:EDIT_VARIABLE",
  Project_Folder_Delete = "PROJECT::FOLDER:DELETE",
  Project_Folder_DeleteVariable = "PROJECT::FOLDER:DELETE_VARIABLE",

  // Docs > Template
  Docs_Template_Grant = "DOCS::TEMPLATE:GRANT",
  Docs_Template_Create = "DOCS::TEMPLATE:CREATE",
  Docs_Template_View = "DOCS::TEMPLATE:VIEW",
  Docs_Template_Edit = "DOCS::TEMPLATE:EDIT",
  Docs_Template_Delete = "DOCS::TEMPLATE:DELETE",

  // Docs > Folder
  Docs_Folder_Grant = "DOCS::FOLDER:GRANT",
  Docs_Folder_CreateFolder = "DOCS::FOLDER:CREATE_FOLDER",
  Docs_Folder_CreateVariable = "DOCS::FOLDER:CREATE_VARIABLE",
  Docs_Folder_CreateDocument = "DOCS::FOLDER:CREATE_DOCUMENT",
  Docs_Folder_CreateProposal = "DOCS::FOLDER:CREATE_PROPOSAL",
  Docs_Folder_View = "DOCS::FOLDER:VIEW",
  Docs_Folder_ViewVariable = "DOCS::FOLDER:VIEW_VARIABLE",
  Docs_Folder_Edit = "DOCS::FOLDER:EDIT",
  Docs_Folder_EditVariable = "DOCS::FOLDER:EDIT_VARIABLE",
  Docs_Folder_Delete = "DOCS::FOLDER:DELETE",
  Docs_Folder_DeleteVariable = "DOCS::FOLDER:DELETE_VARIABLE",

  // Docs > FolderFile
  Docs_FolderFile_Grant = "DOCS::FOLDER_FILE:GRANT",
  Docs_FolderFile_View = "DOCS::FOLDER_FILE:VIEW",
  Docs_FolderFile_Edit = "DOCS::FOLDER_FILE:EDIT",
  Docs_FolderFile_Delete = "DOCS::FOLDER_FILE:DELETE",

  // Docs > Proposal
  Docs_Proposal_Grant = "DOCS::PROPOSAL:GRANT",
  Docs_Proposal_CreateVersion = "DOCS::PROPOSAL:CREATE_VERSION",
  Docs_Proposal_CreateComment = "DOCS::PROPOSAL:CREATE_COMMENT",
  Docs_Proposal_LibrarySearch = "DOCS::PROPOSAL:LIBRARY_SEARCH",
  Docs_Proposal_View = "DOCS::PROPOSAL:VIEW",
  Docs_Proposal_ViewComment = "DOCS::PROPOSAL:VIEW_COMMENT",
  Docs_Proposal_Export = "DOCS::PROPOSAL:EXPORT",
  Docs_Proposal_Edit = "DOCS::PROPOSAL:EDIT",
  Docs_Proposal_Delete = "DOCS::PROPOSAL:DELETE",
  Docs_Proposal_DeleteComment = "DOCS::PROPOSAL:DELETE_COMMENT",

  // Docs > Document
  Docs_Document_Grant = "DOCS::DOCUMENT:GRANT",
  Docs_Document_View = "DOCS::DOCUMENT:VIEW",
  Docs_Document_Delete = "DOCS::DOCUMENT:DELETE",
  Docs_Document_CreateEsign = "DOCS::DOCUMENT:CREATE_ESIGN",

  // Docs > Sign
  Docs_DocumentSign_Cancel = "DOCS::DOCUMENT_SIGN:CANCEL",
  Docs_DocumentSign_Signer = "DOCS::DOCUMENT_SIGN:SIGNER",

  // Builder
  Builder_Study_Grant = "BUILDER::STUDY:GRANT",
  Builder_Study_View = "BUILDER::STUDY:VIEW",
  Builder_Study_Edit = "BUILDER::STUDY:EDIT",

  // Safety
  Safety_Workspace_Grant = "SAFETY::WORKSPACE:GRANT",
  Safety_Workspace_Edit = "SAFETY::WORKSPACE:EDIT",
  Safety_Workspace_Blinder = "SAFETY::WORKSPACE:BLINDER",
  Safety_Workspace_View = "SAFETY::WORKSPACE:VIEW",

  // SAFETY > Workspace Item
  Safety_WorkspaceItem_Grant = "SAFETY::WORKSPACE_ITEM:GRANT",
  Safety_WorkspaceItem_Edit = "SAFETY::WORKSPACE_ITEM:EDIT",
  Safety_WorkspaceItem_View = "SAFETY::WORKSPACE_ITEM:VIEW",

  // OCR > Template
  OCR_Template_Grant = "OCR::TEMPLATE:GRANT",
  OCR_Template_Create = "OCR::TEMPLATE:CREATE",
  OCR_Template_View = "OCR::TEMPLATE:VIEW",
  OCR_Template_Edit = "OCR::TEMPLATE:EDIT",
  OCR_Template_Delete = "OCR::TEMPLATE:DELETE",
}

export type RefId = bigint;

export type CreateRefIdsResponse = RefId[];

export type CreateRefIdsRequest = CreateRefIdsRequestByCount | CreateRefIdsRequestByTypes;

export interface CreateRefIdsRequestByCount {
  count: number;
  service: RefServiceType;
  type: RefResourceType;
}

export interface CreateRefIdByType {
  service: RefServiceType;
  type: RefResourceType;
}

export interface CreateRefIdsRequestByTypes {
  resources: Array<CreateRefIdByType>;
}

export interface UpdateRefsCreatedRequest {
  refIds: RefId[];
  service: RefServiceType;
}

export interface UpdateRefsCreatedResponse {
  updated: boolean;
}

export interface UpdateRefsDeletedRequest {
  refIds: RefId[];
  service: RefServiceType;
}

export interface UpdateRefDeletedResponse {
  updated: boolean;
}

export interface PutRefsOwnerUserIdRequest {
  refIds: RefId[];
  transferrerUserId: bigint;
  receiverUserId: bigint;
  service: RefServiceType;
}

export interface PutRefsOwnerUserIdResponse {
  transferred: boolean;
}
